/**
 * Heritage at Risk lookup.
 * Loads the pre-fetched Heritage at Risk Register 2025 (England, 4,886 sites)
 * and checks if a given coordinate matches a site in the register.
 * Used as enrichment — adds a "Heritage at Risk" badge to detail pages.
 */

interface HAREntry {
  id: string;
  name: string;
  type: string;
  lng: number;
  lat: number;
  sourceUrl: string;
}

let _harCache: HAREntry[] | null = null;
let _harCoordSet: Set<string> | null = null;
let _harLookup: Map<string, HAREntry> | null = null;

async function ensureLoaded(): Promise<void> {
  if (_harCache) return;
  try {
    const res = await fetch("/data/heritage-at-risk.json");
    if (!res.ok) return;
    _harCache = await res.json();
    _harCoordSet = new Set<string>();
    _harLookup = new Map<string, HAREntry>();
    for (const entry of _harCache!) {
      // Round to 3 decimals (~111m) for fuzzy matching
      const key = `${entry.lat.toFixed(3)},${entry.lng.toFixed(3)}`;
      _harCoordSet.add(key);
      _harLookup.set(key, entry);
    }
  } catch { /* silent */ }
}

/**
 * Check if a site at the given coordinates is in the Heritage at Risk Register.
 * Returns the HAR entry if found, or null if not.
 */
export async function checkHeritageAtRisk(lat: number, lng: number): Promise<{ atRisk: true; url: string } | null> {
  await ensureLoaded();
  if (!_harCoordSet || !_harLookup) return null;

  // Check exact match (3 decimal precision = ~111m)
  const key = `${lat.toFixed(3)},${lng.toFixed(3)}`;
  if (_harCoordSet.has(key)) {
    const entry = _harLookup.get(key)!;
    return { atRisk: true, url: entry.sourceUrl };
  }

  // Check nearby (try ±0.001 degree offsets for slight coordinate differences)
  for (const dLat of [-0.001, 0, 0.001]) {
    for (const dLng of [-0.001, 0, 0.001]) {
      if (dLat === 0 && dLng === 0) continue;
      const nearKey = `${(lat + dLat).toFixed(3)},${(lng + dLng).toFixed(3)}`;
      if (_harCoordSet.has(nearKey)) {
        const entry = _harLookup.get(nearKey)!;
        return { atRisk: true, url: entry.sourceUrl };
      }
    }
  }

  return null;
}
